import { Dialog, Transition } from '@headlessui/react';
import { Dispatch, FC, Fragment, SetStateAction } from 'react';
import { RenderAssociationEntityDetails } from '@/modules/entity/components/entity-info/AssociationEntityInfo.tsx';
import { RenderCompanyEntityDetails } from '@/modules/entity/components/entity-info/CompanyEntityInfo.tsx';
import { RenderIndividualEntityDetails } from '@/modules/entity/components/entity-info/IndividualEntityInfo.tsx';
import { RenderPartnershipEntityDetails } from '@/modules/entity/components/entity-info/PartnershipEntityInfo.tsx';
import { RenderSmsfEntityDetails } from '@/modules/entity/components/entity-info/SmsfEntityInfo.tsx';
import { RenderTrustEntityDetails } from '@/modules/entity/components/entity-info/TrustEntityInfo.tsx';
import { EntityWithDocumentFullResponse } from '@/modules/entity/types/EntityItemResponse.ts';
import { Avatar } from 'ui';
import { CloseIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';
import { EntityType } from 'api';

export interface AdminEntityInfoModalProps extends Partial<EntityWithDocumentFullResponse> {
  isOpened: boolean;
  handleCloseModal: Dispatch<SetStateAction<boolean>>;
}

const entityInfo: Record<EntityType, FC<Partial<EntityWithDocumentFullResponse>>> = {
  individual: RenderIndividualEntityDetails,
  company: RenderCompanyEntityDetails,
  trust: RenderTrustEntityDetails,
  smsf: RenderSmsfEntityDetails,
  partnership: RenderPartnershipEntityDetails,
  association: RenderAssociationEntityDetails,
};

export const AdminEntityInfoModal = ({
  isOpened,
  handleCloseModal,
  entity_name,
  entity_id,
  id,
  type,
  ...entity
}: AdminEntityInfoModalProps) => {
  const { avatar_url, first_name, last_name } = entity.user || {};

  const renderEntityInfo = () => {
    if (!type) return null;

    const Component = entityInfo[type];

    return <Component id={id} type={type} entity_name={entity_name} {...entity} />;
  };

  return (
    <Transition appear show={isOpened} as={Fragment}>
      <Dialog as="div" className="relative z-10 right-0" onClose={() => handleCloseModal(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 right-0 overflow-y-auto">
          <div className="flex h-screen w-full items-center justify-end text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full h-full max-w-2xl py-[22px] p-4 sm:px-8 space-y-[18px] transform overflow-scroll bg-white text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="div" className="w-full flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <Avatar src={avatar_url} firstName={first_name} lastName={last_name} />
                    <div className="flex flex-col">
                      <p className="text-[14px] leading-[22px] font-medium">{entity_name}</p>
                      <p className="text-[12px] leading-[20px] text-gray-500 space-x-2 divide-x divide-gray-200">
                        <span>{fixNameEnum(type)}</span>
                        <span className="pl-2">{entity_id}</span>
                      </p>
                    </div>
                  </div>
                  <button onClick={() => handleCloseModal(false)}>
                    <img src={CloseIcon} alt="Loading..." />
                  </button>
                </Dialog.Title>
                {renderEntityInfo()}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
